import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Box } from "@mui/material";
import { tss } from "tss-react/mui";
import { StyledDivider, StyledFormCheckbox } from "components/StyledUI";
import { formatCurrency } from "tools/stringUtils";
import {
  selectCheckedSellersSelector,
  selectCheckedTotalsSelector,
  selectGroupedBasketsBySeller,
  selectSellerValidations,
} from "_redux/selectors/order";
import { isEqual } from "lodash-es";
import { useAppDispatch, useAppSelector } from "_redux/hooks";

import { BasketValidationTypeEnum } from "@efood-commerce-sdk/typescript-axios-transaction-efood";
import {
  CALCULATE_CHECKED_TOTALS_ACTION,
  SET_CHECKED_SELLER_ACTION,
  TOGGLE_CHECKED_SELLER_ACTION,
} from "_redux/actions/order";
import CartPromotionCode from "./CartPromotionCode";
import { OrderNowButton } from "../CartButtons";
import TermsHint from "./TermsHint";
import CartOverviewPriceSummary from "./CartOverviewPriceSummary";

const useStyles = tss.create(({ theme }) => ({
  root: {
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    margin: theme.spacing(1, 0),
  },
  fullWidthDivider: {
    marginLeft: `-${theme.spacing(2)}`,
    marginRight: `-${theme.spacing(2)}`,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    flexWrap: "nowrap",
  },
  amount: {
    flexShrink: 0,
    fontSize: 18,
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
  },
  textDisabled: {
    color: theme.palette.text.disabled,
  },
  sellerLabelWrapper: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  sellerLabel: {
    fontSize: 18,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "180px",
  },
}));

interface CartOverviewDialogProps {
  orderId: string;
}

const CartOverview: FC<CartOverviewDialogProps> = ({ orderId }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const sellers = useAppSelector(selectGroupedBasketsBySeller, isEqual);
  const sellerValidations = useAppSelector(selectSellerValidations, isEqual);
  const checkedSellers = useAppSelector(selectCheckedSellersSelector, isEqual);
  const totals = useAppSelector(selectCheckedTotalsSelector, isEqual);

  const hasAnySellerChecked = useMemo(
    () => Object.values(checkedSellers).some((v) => v),
    [checkedSellers]
  );

  const handleToggle = useCallback(
    (id: string) => {
      dispatch(TOGGLE_CHECKED_SELLER_ACTION(id));
    },
    [dispatch]
  );

  useEffect(() => {
    // Initialize checked sellers if none is there
    if (Object.keys(checkedSellers ?? {}).length === 0) {
      const initialChecked: Record<string, boolean> = sellers.reduce(
        (acc, seller) => {
          const hasDenial = sellerValidations[seller.identifier]?.some(
            (val) => val.validation.type === BasketValidationTypeEnum.Denial
          );
          acc[seller.identifier] = !hasDenial;
          return acc;
        },
        {} as Record<string, boolean>
      );

      dispatch(SET_CHECKED_SELLER_ACTION(initialChecked));
      dispatch(CALCULATE_CHECKED_TOTALS_ACTION());
    }
  }, [checkedSellers, sellers, sellerValidations, dispatch]);

  // Recalculate totals when baskets change
  useEffect(() => {
    dispatch(CALCULATE_CHECKED_TOTALS_ACTION());
  }, [sellers, dispatch]);

  // Filter sellers with denial validation
  const sellersWithDenial = useMemo(
    () =>
      sellers.filter((seller) =>
        sellerValidations[seller.identifier]?.some(
          (val) => val.validation.type === BasketValidationTypeEnum.Denial
        )
      ),
    [sellers, sellerValidations]
  );

  // Filter sellers without denial validation
  const sellersWithoutDenial = useMemo(
    () => sellers.filter((seller) => !sellersWithDenial.includes(seller)),
    [sellers, sellersWithDenial]
  );

  return (
    <Box className={classes.root}>
      <Typography variant="h3" mb={1}>
        {t("cart.overview")}
      </Typography>

      <StyledDivider light className={classes.fullWidthDivider} pb={0} />
      {sellersWithoutDenial.map((seller) => (
        <Box key={seller.identifier} className={classes.row}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              minWidth: 0,
            }}>
            <StyledFormCheckbox
              sx={{ flex: 1 }}
              checkboxProps={{
                name: seller.identifier,
                checked: checkedSellers[seller.identifier] ?? false,
                onChange: () => handleToggle(seller.identifier),
              }}
              label={
                <Box className={classes.sellerLabelWrapper}>
                  <Box>
                    <Typography
                      className={classes.sellerLabel}
                      title={seller.displayName}>
                      {seller.displayName}
                    </Typography>
                    {sellerValidations[seller.identifier]?.some(
                      (val) =>
                        val.validation.type === BasketValidationTypeEnum.Warn
                    ) && (
                      <Typography className={classes.textDisabled}>
                        {t("cart.orderPleaseNote")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
          </Box>
          <Typography className={classes.amount}>
            {formatCurrency(seller.total)}
          </Typography>
        </Box>
      ))}

      {sellersWithDenial.length > 0 && (
        <Typography
          variant="body1"
          fontWeight="normal"
          mt={2}
          mb={-1}
          className={classes.textDisabled}>
          {t("cart.orderNotPossible")}
        </Typography>
      )}

      {sellersWithDenial.map((seller) => (
        <Box key={seller.identifier} className={classes.row}>
          <StyledFormCheckbox
            checkboxProps={{
              name: seller.identifier,
              checked: false,
              disabled: true,
              onChange: () => {},
            }}
            label={
              <span className={classes.sellerLabel}>{seller.displayName}</span>
            }
          />
          <Typography className={`${classes.amount} ${classes.textDisabled}`}>
            {formatCurrency(seller.total)}
          </Typography>
        </Box>
      ))}

      <StyledDivider light className={classes.fullWidthDivider} pb={1} />

      <CartPromotionCode orderId={orderId} />

      <CartOverviewPriceSummary {...totals} />

      <Box pb={1}>
        <TermsHint type="overview" />
      </Box>

      <OrderNowButton fullWidth disabled={!hasAnySellerChecked} />
    </Box>
  );
};

export default CartOverview;
